<script setup lang="ts">
import {
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  instanceOfDiscreteNumericValueDto,
  instanceOfElectronBeamQualityCombinationValueDto,
  instanceOfEnumerationValueDto,
  instanceOfNumericValueDto,
  instanceOfPhotonBeamQualityCombinationValueDto,
  instanceOfTextValueDto,
  type ISystemSetupCombinationValue,
} from '@/api/generator';
import NumericValueField from '@/components/forms/dataTypes/NumericValueField.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { translateEntityProperty } from '@/utils/helper';
import { toDisplayValue } from '@/utils/numberUtils';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import { isNumber } from 'lodash';

defineProps<{
  item: ISystemSetupCombinationValue;
}>();
</script>

<template>
  <template
    v-if="
      instanceOfElectronBeamQualityCombinationValueDto(item) ||
      instanceOfPhotonBeamQualityCombinationValueDto(item)
    "
  >
    {{ item.beamQualityName }}
  </template>

  <template v-else-if="instanceOfBeamShapingGroupRectangularDto(item)">
    <v-table class="table-no-borders table-fit-content justify-self-end">
      <tbody>
        <tr>
          <th scope="row">
            {{ getSetupParameterName(item.radiationFieldSizeX) }}
          </th>
          <td>
            <NumericValueField :modelValue="item.radiationFieldSizeX" mode="details" />
          </td>
        </tr>
        <tr>
          <th scope="row">
            {{ getSetupParameterName(item.radiationFieldSizeY) }}
          </th>
          <td>
            <NumericValueField :modelValue="item.radiationFieldSizeY" mode="details" />
          </td>
        </tr>
      </tbody>
    </v-table>
  </template>

  <template v-else-if="instanceOfBeamShapingGroupCircularDto(item)">
    <v-table class="table-no-borders table-fit-content justify-self-end">
      <thead>
        <tr></tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">
            {{ getSetupParameterName(item.radiationFieldSize) }}
          </th>
          <td>
            <NumericValueField mode="details" :modelValue="item.radiationFieldSize" />
          </td>
        </tr>
      </tbody>
    </v-table>
  </template>

  <template v-else-if="instanceOfNumericValueDto(item) && isNumber(item.value)">
    {{
      toDisplayValue(item.value, {
        fractionDigits: item.decimalPlaces,
        unitAbbreviation: item.unitAbbreviation,
        valueType: item.valueType,
      })
    }}
  </template>

  <template v-else-if="instanceOfEnumerationValueDto(item)">
    {{ item.value?.name ? translateEntityProperty('name', item.value) : EMPTY_FIELD_SIGN }}
  </template>

  <template v-else-if="instanceOfTextValueDto(item) && item.value">
    {{ item.value }}
  </template>

  <template v-else-if="instanceOfDiscreteNumericValueDto(item) && isNumber(item.value)">
    {{
      toDisplayValue(item.value, {
        fractionDigits: item.decimalPlaces,
        unitAbbreviation: item.unitAbbreviation,
        valueType: item.valueType,
      })
    }}
  </template>

  <template v-else>
    {{ EMPTY_FIELD_SIGN }}
  </template>
</template>

<style scoped lang="scss">
.no-line-break {
  white-space: nowrap;
}
</style>
